/**
 * Theme Context
 * Provides theme state management with localStorage persistence
 * Supports: Light, Dark, System preference, Auto (time-based)
 */

import React, { createContext, useState, useEffect, useCallback } from 'react';
import {
  THEMES,
  THEME_STORAGE_KEY,
  DEFAULT_THEME_MODE,
  getResolvedThemeName,
  getSystemTheme,
  getTimeBasedTheme
} from '../config/themeConfig';

// Create the context
export const ThemeContext = createContext({
  themeMode: DEFAULT_THEME_MODE,
  resolvedTheme: THEMES.LIGHT,
  isDark: false,
  setThemeMode: () => {},
  toggleTheme: () => {}
});

// Theme Provider Component
export const ThemeProvider = ({ children }) => {
  // Initialize from localStorage or default
  const [themeMode, setThemeModeState] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored && Object.values(THEMES).includes(stored)) {
        return stored;
      }
    }
    return DEFAULT_THEME_MODE;
  });

  // Resolved theme (actual light/dark)
  const [resolvedTheme, setResolvedTheme] = useState(() =>
    getResolvedThemeName(themeMode)
  );

  // Update resolved theme when mode changes
  const updateResolvedTheme = useCallback(() => {
    const resolved = getResolvedThemeName(themeMode);
    setResolvedTheme(resolved);
  }, [themeMode]);

  // Apply theme to document
  const applyTheme = useCallback((themeName) => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', themeName);
      document.body.setAttribute('data-theme', themeName);

      // Also update meta theme-color for mobile browsers
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        metaThemeColor.setAttribute(
          'content',
          themeName === 'dark' ? '#252b3b' : '#3d2817'
        );
      }
    }
  }, []);

  // Set theme mode with persistence
  const setThemeMode = useCallback((mode) => {
    if (Object.values(THEMES).includes(mode)) {
      setThemeModeState(mode);
      localStorage.setItem(THEME_STORAGE_KEY, mode);
    }
  }, []);

  // Toggle between light and dark
  const toggleTheme = useCallback(() => {
    const newMode = resolvedTheme === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK;
    setThemeMode(newMode);
  }, [resolvedTheme, setThemeMode]);

  // Listen for system preference changes
  useEffect(() => {
    if (themeMode === THEMES.SYSTEM && typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

      const handleChange = () => {
        updateResolvedTheme();
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [themeMode, updateResolvedTheme]);

  // Auto mode: Check time periodically
  useEffect(() => {
    if (themeMode === THEMES.AUTO) {
      // Check immediately
      updateResolvedTheme();

      // Check every minute for time-based switching
      const interval = setInterval(() => {
        const currentResolved = getTimeBasedTheme();
        if (currentResolved !== resolvedTheme) {
          updateResolvedTheme();
        }
      }, 60000); // Check every minute

      return () => clearInterval(interval);
    }
  }, [themeMode, resolvedTheme, updateResolvedTheme]);

  // Update resolved theme when mode changes
  useEffect(() => {
    updateResolvedTheme();
  }, [themeMode, updateResolvedTheme]);

  // Apply theme to DOM whenever resolved theme changes
  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme, applyTheme]);

  // Context value
  const value = {
    themeMode,
    resolvedTheme,
    isDark: resolvedTheme === THEMES.DARK,
    setThemeMode,
    toggleTheme,
    THEMES
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
